/**
 * A tiny, safe markdown subset for help-note bodies — rendered as React nodes,
 * never HTML, so there is zero injection surface (no dangerouslySetInnerHTML, no
 * sanitiser dependency).
 *
 * Supported: paragraphs, hard line breaks, ``**bold**``, ``*italic*``,
 * ``\`code\```, ``[text](url)`` links (http/https/mailto/relative/anchor only),
 * ``- `` / ``* `` bullet lists, ``## ``/``### `` headings, and pipe
 * tables (``| a | b |`` with a ``|---|---|`` separator row).
 *
 * Headings start at ``<h2>``: every page that renders this owns its own
 * ``<h1>`` (help-note title, legal-document title), so a body heading is
 * always a section *inside* that — emitting ``<h1>`` here would break
 * heading order for screen readers. A lone ``# `` is accepted as an
 * alias for ``## `` rather than rendering literally, because an author
 * writing plain markdown reaches for it first.
 *
 * Deliberately NOT supported:
 *  - ``_underscore_`` emphasis — this admin is full of ``snake_case`` identifiers
 *    (``page_id``, ``order_id``); treating ``_`` as markup would wreck them.
 *    Put identifiers in backticks instead.
 *  - Raw HTML — passed through as literal text.
 * Emphasis also requires the marker to hug non-space (CommonMark flanking), so
 * "cena * 2" stays literal.
 */

import { type ReactNode } from "react";

const SAFE_HREF = /^(https?:\/\/|mailto:|\/|#)/i;

function safeLink(text: string, url: string, key: string): ReactNode {
  if (!SAFE_HREF.test(url)) return text; // unknown/unsafe scheme → drop the link
  const external = /^https?:\/\//i.test(url);
  return (
    <a
      key={key}
      href={url}
      className="underline"
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {text}
    </a>
  );
}

interface Inline {
  re: RegExp;
  render: (m: RegExpExecArray, key: string) => ReactNode;
}

// Ordered by precedence — code first so backtick content is always literal.
const INLINE: Inline[] = [
  {
    re: /`([^`]+)`/,
    render: (m, k) => (
      <code
        key={k}
        className="rounded bg-ink-5/30 px-1 py-px font-mono text-[0.92em]"
      >
        {m[1]}
      </code>
    ),
  },
  {
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    render: (m, k) => safeLink(m[1], m[2], k),
  },
  {
    re: /\*\*([^\s*](?:[^*]*[^\s*])?)\*\*/,
    render: (m, k) => <strong key={k}>{parseInline(m[1], k)}</strong>,
  },
  {
    re: /\*([^\s*](?:[^*]*[^\s*])?)\*/,
    render: (m, k) => <em key={k}>{parseInline(m[1], k)}</em>,
  },
];

function parseInline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest) {
    let best: { i: Inline; m: RegExpExecArray } | null = null;
    for (const inline of INLINE) {
      const m = inline.re.exec(rest);
      if (m && (best === null || m.index < best.m.index)) best = { i: inline, m };
    }
    if (best === null) {
      out.push(rest);
      break;
    }
    if (best.m.index > 0) out.push(rest.slice(0, best.m.index));
    out.push(best.i.render(best.m, `${keyBase}-${n++}`));
    rest = rest.slice(best.m.index + best.m[0].length);
  }
  return out;
}

function paragraph(lines: string[], key: string): ReactNode {
  const kids: ReactNode[] = [];
  lines.forEach((line, idx) => {
    if (idx > 0) kids.push(<br key={`${key}-br${idx}`} />);
    kids.push(...parseInline(line, `${key}-l${idx}`));
  });
  return <p key={key}>{kids}</p>;
}

export function renderHelpMarkdown(src: string): ReactNode {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: string[] = [];
  let b = 0;

  const flushPara = () => {
    if (para.length) {
      blocks.push(paragraph(para, `p${b++}`));
      para = [];
    }
  };
  // Tabulka se pozná až podle ODDĚLOVAČE na druhém řádku: samotné
  // ``|`` v textu (typicky v ceníku nebo v cestě) tabulku dělat nesmí.
  const isSeparator = (line: string) =>
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(line);
  const cells = (line: string) =>
    line.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());

  const flushList = () => {
    if (list.length) {
      const items = list;
      blocks.push(
        <ul key={`u${b++}`} className="list-disc pl-5">
          {items.map((it, idx) => (
            <li key={idx}>{parseInline(it, `u${idx}`)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };

  for (let idx = 0; idx < lines.length; idx += 1) {
    const line = lines[idx];

    // Tabulka: hlavička + oddělovač + řádky, dokud začínají ``|``.
    // Právní texty jí popisují účel vs. právní titul; bez ní by se
    // z toho staly odstavce plné svislítek.
    if (
      line.trim().startsWith("|") &&
      idx + 1 < lines.length &&
      isSeparator(lines[idx + 1])
    ) {
      flushPara();
      flushList();
      const head = cells(line);
      const body: string[][] = [];
      let j = idx + 2;
      while (j < lines.length && lines[j].trim().startsWith("|")) {
        body.push(cells(lines[j]));
        j += 1;
      }
      blocks.push(
        <div key={`t${b}`} className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr>
                {head.map((c, i) => (
                  <th
                    key={i}
                    className="border-b border-border py-1.5 pr-4 font-semibold text-ink"
                  >
                    {parseInline(c, `t${b}h${i}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, r) => (
                <tr key={r}>
                  {row.map((c, i) => (
                    <td
                      key={i}
                      className="border-b border-border py-1.5 pr-4 align-top"
                    >
                      {parseInline(c, `t${b}r${r}c${i}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      b += 1;
      idx = j - 1;
      continue;
    }

    // Headings before bullets: ``*`` starts a list, ``#`` never does, so
    // the two patterns can't collide — but order keeps it obvious.
    const heading = /^\s*(#{1,3})\s+(.*\S)\s*$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (heading) {
      flushPara();
      flushList();
      // ``#`` and ``##`` both land on h2 (see module docstring: the page
      // owns the h1), ``###`` on h3.
      const level = heading[1].length >= 3 ? 3 : 2;
      const Tag = level === 3 ? "h3" : "h2";
      blocks.push(
        <Tag
          key={`h${b++}`}
          className={
            level === 3
              ? "mt-4 text-sm font-semibold text-ink"
              : "mt-6 text-base font-semibold text-ink"
          }
        >
          {parseInline(heading[2], `h${b}`)}
        </Tag>,
      );
    } else if (bullet) {
      flushPara();
      list.push(bullet[1]);
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();

  return <>{blocks}</>;
}
